import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Table } from 'react-bootstrap';
import axios from '../../../../api/axios';
import EditOrderModal from '../../../../utils/modals/orders/EditOrderModal';
import CreateOrderModal from '../../../../utils/modals/orders/CreateOrderModal';
import ViewOrderModal from '../../../../utils/modals/orders/ViewOrderModal';
import { GlobalContext } from '../../../../store/GlobalContext';
import { getOrderStatusMeta, ORDER_STATUS_OPTIONS } from '../../../../utils/orderStatus';

type OrderRow = {
    _id: string;
    orderNumber: string;
    customerInfo: {
        name: string;
        email?: string | null;
        phone: string;
        address: string;
    };
    items: Array<{
        _id: string;
        productId: string;
        productName: string;
        quantity: number;
        price: number;
    }>;
    totalAmount: number;
    status: string;
    paymentStatus: 'paid' | 'unpaid';
    paymentMethod?: string;
    trackingNumber?: string;
    deliveryBoyUserId?: string;
    deliveryNote?: string;
    failureReason?: string;
    proofOfDeliveryUrl?: string;
    deliveryBoy?: {
        id: string;
        name?: string;
        phoneNumber?: string | null;
    } | null;
    createdAt?: string;
};

type DeliveryBoyOption = {
    _id: string;
    name: string;
    phoneNumber?: string | null;
};

type DeliveryUserOption = {
    _id: string;
    username: string;
    email?: string;
    firstName?: string;
    lastName?: string;
};

type PendingSummary = {
    totalPendingAmount: number;
    totalPendingOrders: number;
    customers: Array<{
        customerId: string;
        customerName: string;
        customerPhone: string;
        customerEmail: string | null;
        pendingOrders: number;
        pendingAmount: number;
    }>;
};

type DeliverySummary = {
    totalOrders: number;
    placed: number;
    accepted: number;
    packing: number;
    outForDelivery: number;
    delivered: number;
    cancelled: number;
    dueToday: number;
    overdue: number;
};

const Orders = () => {
    const { state } = useContext(GlobalContext);
    const token = state?.auth?.token;

    const [show, setShow] = useState(false);
    const [orders, setOrders] = useState<OrderRow[]>([]);
    const [products, setProducts] = useState<Array<{ _id: string; name: string; price: number }>>([]);
    const [deliveryBoys, setDeliveryBoys] = useState<DeliveryBoyOption[]>([]);
    const [deliveryUsers, setDeliveryUsers] = useState<DeliveryUserOption[]>([]);
    const [editOrder, setEditOrder] = useState<OrderRow | null>(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [viewOrder, setViewOrder] = useState<OrderRow | null>(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [pendingSummary, setPendingSummary] = useState<PendingSummary | null>(null);
    const [deliverySummary, setDeliverySummary] = useState<DeliverySummary | null>(null);

    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [paymentFilter, setPaymentFilter] = useState<'all' | 'paid' | 'unpaid'>('all');
    const [sort, setSort] = useState<'newest' | 'oldest' | 'totalAmount_desc' | 'totalAmount_asc'>('newest');

    const handleEditClick = (order: OrderRow) => {
        setEditOrder(order);
        setShowEditModal(true);
    };

    const handleViewClick = (order: OrderRow) => {
        setViewOrder(order);
        setShowViewModal(true);
    };

    const queryParams = useMemo(() => {
        const params: Record<string, string | number> = {
            page,
            limit,
            sort,
        };
        if (search.trim()) {
            params.search = search.trim();
        }
        if (statusFilter !== 'all') {
            params.status = statusFilter;
        }
        if (paymentFilter !== 'all') {
            params.paymentStatus = paymentFilter;
        }
        return params;
    }, [page, limit, search, statusFilter, paymentFilter, sort]);

    const fetchOrders = useCallback(async () => {
        if (!token) return;
        try {
            const response = await axios.get('/orders', {
                headers: { Authorization: `Bearer ${token}` },
                params: queryParams,
            });
            const normalizedOrders = Array.isArray(response.data?.orders)
                ? response.data.orders
                : Array.isArray(response.data)
                    ? response.data
                    : [];
            setOrders(normalizedOrders);
            setTotalPages(typeof response.data?.totalPages === 'number' ? response.data.totalPages : 0);
        } catch (err: any) {
            setError(err?.response?.data?.message || 'Failed to fetch orders.');
            setOrders([]);
            setTotalPages(0);
        }
    }, [token, queryParams]);

    const fetchProducts = useCallback(async () => {
        try {
            const response = await axios.get('/products', {
                params: { page: 1, limit: 100, isActive: true, sortBy: 'name', sortOrder: 'asc' },
            });
            setProducts(Array.isArray(response.data?.products) ? response.data.products : []);
        } catch (err: any) {
            setProducts([]);
            setError(err?.response?.data?.message || 'Failed to fetch products for order creation.');
        }
    }, []);

    const fetchPendingSummary = useCallback(async () => {
        if (!token) return;
        try {
            const response = await axios.get('/orders/pending-payments', {
                headers: { Authorization: `Bearer ${token}` },
            });
            setPendingSummary(response.data);
        } catch {
            setPendingSummary(null);
        }
    }, [token]);

    const fetchDeliverySummary = useCallback(async () => {
        if (!token) return;
        try {
            const response = await axios.get('/orders/delivery/summary', {
                headers: { Authorization: `Bearer ${token}` },
            });
            setDeliverySummary(response.data);
        } catch {
            setDeliverySummary(null);
        }
    }, [token]);

    const fetchDeliveryBoys = useCallback(async () => {
        if (!token) return;
        try {
            const response = await axios.get('/orders/delivery-boys', {
                headers: { Authorization: `Bearer ${token}` },
            });

            const list = Array.isArray(response.data?.deliveryBoys)
                ? response.data.deliveryBoys
                : Array.isArray(response.data)
                    ? response.data
                    : [];
            setDeliveryBoys(list);
        } catch {
            setDeliveryBoys([]);
        }
    }, [token]);

    const fetchDeliveryUsers = useCallback(async () => {
        if (!token) return;
        try {
            const response = await axios.get('/users/delivery-team', {
                headers: { Authorization: `Bearer ${token}` },
            });
            const list = Array.isArray(response.data) ? response.data : [];
            setDeliveryUsers(list);
        } catch {
            setDeliveryUsers([]);
        }
    }, [token]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    useEffect(() => {
        fetchProducts();
        fetchPendingSummary();
        fetchDeliverySummary();
        fetchDeliveryBoys();
        fetchDeliveryUsers();
    }, [fetchProducts, fetchPendingSummary, fetchDeliverySummary, fetchDeliveryBoys, fetchDeliveryUsers]);

    useEffect(() => {
        if (!error && !success) return;
        const timer = setTimeout(() => {
            setError('');
            setSuccess('');
        }, 3000);
        return () => clearTimeout(timer);
    }, [error, success]);

    const handleDelete = async (orderId: string) => {
        if (!token) {
            setError('Authentication is required.');
            return;
        }
        if (!window.confirm('Are you sure you want to delete this order?')) return;

        try {
            await axios.delete(`/orders/${orderId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setSuccess('Order deleted successfully.');
            await fetchOrders();
            await fetchPendingSummary();
            await fetchDeliverySummary();
        } catch (err: any) {
            setError(err?.response?.data?.message || 'Failed to delete order.');
        }
    };

    return (
        <>
            <CreateOrderModal
                show={show}
                setShow={setShow}
                token={token}
                products={products}
                onSaved={async () => {
                    setSuccess('Order created successfully.');
                    await fetchOrders();
                    await fetchPendingSummary();
                    await fetchDeliverySummary();
                }}
            />
            <EditOrderModal
                show={showEditModal}
                setShow={setShowEditModal}
                token={token}
                order={editOrder}
                deliveryBoys={deliveryBoys}
                deliveryUsers={deliveryUsers}
                onSaved={async () => {
                    setSuccess('Order updated successfully.');
                    await fetchOrders();
                    await fetchPendingSummary();
                    await fetchDeliverySummary();
                }}
            />

            <ViewOrderModal
                show={showViewModal}
                setShow={setShowViewModal}
                order={viewOrder}
            />

            <Card className="p-3 mb-3 admin-section-card">
                <div className="mb-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <h4 className="mb-0">Orders</h4>
                    <Button variant="dark" onClick={() => setShow(true)}>Create an Order</Button>
                </div>

                {error && <Alert variant="danger">{error}</Alert>}
                {success && <Alert variant="success">{success}</Alert>}

                <div className="d-flex flex-wrap gap-2 mb-3 admin-toolbar-row">
                    <Form
                        className="d-flex gap-2 flex-grow-1 admin-search-form"
                        onSubmit={(event) => {
                            event.preventDefault();
                            setPage(1);
                            setSearch(searchInput);
                        }}
                    >
                        <Form.Control
                            placeholder="Search order/customer/phone"
                            value={searchInput}
                            onChange={(event) => setSearchInput(event.target.value)}
                        />
                        <Button type="submit" variant="outline-dark">Search</Button>
                        <Button type="button" variant="outline-secondary" onClick={() => { setSearchInput(''); setSearch(''); setPage(1); }}>Clear</Button>
                    </Form>
                    <Form.Select className="admin-filter-select" value={statusFilter} onChange={(event) => { setStatusFilter(event.target.value); setPage(1); }}>
                        <option value="all">All Statuses</option>
                        {ORDER_STATUS_OPTIONS.map((status) => (
                            <option key={status.value} value={status.value}>{status.label}</option>
                        ))}
                    </Form.Select>
                    <Form.Select className="admin-filter-select admin-filter-sm" value={paymentFilter} onChange={(event) => { setPaymentFilter(event.target.value as 'all' | 'paid' | 'unpaid'); setPage(1); }}>
                        <option value="all">All Payments</option>
                        <option value="unpaid">Unpaid</option>
                        <option value="paid">Paid</option>
                    </Form.Select>
                    <Form.Select className="admin-filter-select admin-filter-sm" value={sort} onChange={(event) => { setSort(event.target.value as 'newest' | 'oldest' | 'totalAmount_desc' | 'totalAmount_asc'); setPage(1); }}>
                        <option value="newest">Newest</option>
                        <option value="oldest">Oldest</option>
                        <option value="totalAmount_desc">Amount: High to Low</option>
                        <option value="totalAmount_asc">Amount: Low to High</option>
                    </Form.Select>
                    <Form.Select className="admin-filter-select admin-filter-xs" value={String(limit)} onChange={(event) => { setLimit(Number(event.target.value)); setPage(1); }}>
                        <option value="10">10</option>
                        <option value="20">20</option>
                        <option value="50">50</option>
                    </Form.Select>
                </div>

                <div className="row g-2 mb-3">
                    <div className="col-md-3">
                        <div className="border rounded p-2 admin-stat-tile admin-stat-info">
                            <small className="text-muted">Open Deliveries</small>
                            <div><strong>{
                                Number(deliverySummary?.placed ?? 0)
                                + Number(deliverySummary?.accepted ?? 0)
                                + Number(deliverySummary?.packing ?? 0)
                                + Number(deliverySummary?.outForDelivery ?? 0)
                            }</strong></div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="border rounded p-2 admin-stat-tile admin-stat-success">
                            <small className="text-muted">Delivered</small>
                            <div><strong>{deliverySummary?.delivered ?? 0}</strong></div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="border rounded p-2 admin-stat-tile admin-stat-warning">
                            <small className="text-muted">Due Today</small>
                            <div><strong>{deliverySummary?.dueToday ?? 0}</strong></div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="border rounded p-2 admin-stat-tile admin-stat-danger">
                            <small className="text-muted">Overdue</small>
                            <div><strong>{deliverySummary?.overdue ?? 0}</strong></div>
                        </div>
                    </div>
                </div>

                <div className="mb-3 p-3 border rounded admin-highlight-box">
                    <h6 className="mb-1">Pending COD Collection</h6>
                    <div>Total unpaid COD orders: <strong>{pendingSummary?.totalPendingOrders ?? 0}</strong></div>
                    <div>Total receivable amount: <strong>Rs {(pendingSummary?.totalPendingAmount ?? 0).toFixed(2)}</strong></div>
                </div>
            </Card>

            <div className="mb-2 admin-compact-table">
                {pendingSummary?.customers?.length ? (
                    <Table size="sm" bordered>
                        <thead>
                            <tr>
                                <th>Customer</th>
                                <th>Phone</th>
                                <th>Pending Orders</th>
                                <th>Pending Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pendingSummary.customers.map((customer) => (
                                <tr key={customer.customerId}>
                                    <td>{customer.customerName}</td>
                                    <td>{customer.customerPhone}</td>
                                    <td>{customer.pendingOrders}</td>
                                    <td>Rs {Number(customer.pendingAmount).toFixed(2)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                ) : null}
            </div>

            <Table striped bordered hover responsive className="admin-data-table">
                <thead>
                    <tr>
                        <th>Order #</th>
                        <th>Customer</th>
                        <th>Items</th>
                        <th>Total</th>
                        <th>Status</th>
                        <th>Payment Status</th>
                        <th>Delivery Boy</th>
                        <th>Tracking</th>
                        <th>Payment Method</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {Array.isArray(orders) && orders.length > 0 ? (
                        orders.map((order) => {
                            const statusMeta = getOrderStatusMeta(order.status);

                            return (
                            <tr key={order._id}>
                                <td>
                                    {order.orderNumber}
                                </td>
                                <td>
                                    <div>{order.customerInfo?.name || 'Guest'}</div>
                                    <small>{order.customerInfo?.phone}</small>
                                </td>
                                <td>
                                    {Array.isArray(order.items) && order.items.length > 0 ? (
                                        order.items.map((item, idx) => (
                                            <div key={idx}>
                                                <span>{item.productName}</span> ({item.quantity}) - Rs {Number(item?.price || 0).toFixed(2)}
                                            </div>
                                        ))
                                    ) : 'No Items'}
                                </td>

                                <td>Rs {Number(order.totalAmount || 0).toFixed(2)}</td>
                                <td>
                                    <Badge className='p-2 pt-1' bg={statusMeta.badge}>
                                        {statusMeta.label}
                                    </Badge>
                                    {order.failureReason ? (
                                        <div><small className="text-danger">Reason: {order.failureReason}</small></div>
                                    ) : null}
                                    {order.deliveryNote ? (
                                        <div><small className="text-muted">Note: {order.deliveryNote}</small></div>
                                    ) : null}
                                    {order.proofOfDeliveryUrl ? (
                                        <div>
                                            <a href={order.proofOfDeliveryUrl} target="_blank" rel="noreferrer">Proof</a>
                                        </div>
                                    ) : null}
                                </td>
                                <td>
                                    <Badge className='p-2 pt-1' bg={order.paymentStatus === 'paid' ? 'success' : 'warning'}>
                                        {order.paymentStatus}
                                    </Badge>
                                </td>
                                <td>{order.deliveryBoy?.name || 'Unassigned'}</td>
                                <td>{order.trackingNumber || 'N/A'}</td>
                                <td>{order.paymentMethod || 'N/A'}</td>
                                <td>
                                    <Button className='me-2' variant="outline-dark" size="sm" onClick={() => handleViewClick(order)}>View</Button>
                                    <Button className='me-2' variant="warning" size="sm" onClick={() => handleEditClick(order)}>Edit</Button>
                                    <Button variant="danger" size="sm" onClick={() => handleDelete(order._id)}>Delete</Button>
                                </td>
                            </tr>
                            );
                        })
                    ) : (
                        <tr>
                            <td colSpan={10} className="text-center">No orders available</td>
                        </tr>
                    )}
                </tbody>
            </Table>

            <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2 admin-pagination-row">
                <small>Page {page} of {Math.max(totalPages, 1)}</small>
                <div className="d-flex gap-2">
                    <Button size="sm" variant="outline-secondary" disabled={page <= 1} onClick={() => setPage((prev) => Math.max(1, prev - 1))}>Prev</Button>
                    <Button size="sm" variant="outline-secondary" disabled={totalPages === 0 || page >= totalPages} onClick={() => setPage((prev) => prev + 1)}>Next</Button>
                </div>
            </div>
        </>
    );
}

export default Orders;

